import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShoppingBag, Search, X, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import MenuItemCard from "@/components/MenuItemCard";
import CartDrawer from "@/components/CartDrawer";
import ChatbotWidget from "@/components/ChatbotWidget";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const RICE_BOWL_OPTIONS = [
    {
        id: "rb-001",
        name: "Black Pepper Sausage Rice Bowl",
        price: 94,
    },
    {
        id: "rb-002",
        name: "Fiery Chicken Rice Bowl",
        price: 85,
    },
    {
        id: "rb-003",
        name: "Mango Tango Chicken Rice Bowl",
        price: 80,
    },
    {
        id: "rb-004",
        name: "Garlic Butter Chicken Rice Bowl",
        price: 95,
    },
    {
        id: "rb-005",
        name: "Garlic Parm Chicken Rice Bowl",
        price: 98,
    },
    {
        id: "rb-006",
        name: "Honey Garlic Chicken Rice Bowl",
        price: 96,
    },
    {
        id: "rb-007",
        name: "Teriyaki Chicken Rice Bowl",
        price: 98,
    },
];

const DIPS_OPTIONS = [
    {
        id: "dp-001",
        name: "Oozy Cheese",
        price: 15,
    },
    {
        id: "dp-002",
        name: "Classic Alifano",
        price: 15,
    },
    {
        id: "dp-003",
        name: "Macardo Sauce",
        price: 15,
    },
];

const CRUST_OPTIONS = [
    {
        id: "no-crust",
        name: "No Crust",
        price: 0,
    },
    {
        id: "cheese-stuffed-crust",
        name: "Cheese Stuffed Crust",
        price: 20,
    },
    {
        id: "garlic-butter-stuffed-crust",
        name: "Garlic Butter Stuffed Crust",
        price: 25,
    },
    {
        id: "spicy-stuffed-crust",
        name: "Spicy Stuffed Crust",
        price: 25,
    },
];

export default function MenuPage() {
    const [menu, setMenu] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState(null);
    const [query, setQuery] = useState("");
    const [logoClicks, setLogoClicks] = useState(0);
    const [selectedRiceBowl, setSelectedRiceBowl] = useState(
        RICE_BOWL_OPTIONS[0],
    );
    const [selectedDip, setSelectedDip] = useState(DIPS_OPTIONS[0]);
    const [selectedPizza, setSelectedPizza] = useState(null);
    const [selectedCrust, setSelectedCrust] = useState(CRUST_OPTIONS[0]);

    const sectionRefs = useRef({});
    const { addItem, count, setIsOpen } = useCart();
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/menu")
            .then((res) => {
                const rawItems = (res.data.items || []).filter(
                    (item) => item.category !== "Crust Options",
                );

                // Dips should show as ONE option selector, not 3 duplicate blocks/cards.
                // Even if the backend sends multiple dip items, collapse them into one display-only section.
                const firstDipItem = rawItems.find((item) => item.category === "Dips");
                const cleanItems = rawItems.filter((item) => item.category !== "Dips");

                if (firstDipItem) {
                    cleanItems.push({
                        ...firstDipItem,
                        id: "dp-menu-001",
                        name: "Dips",
                        category: "Dips",
                        price: 0,
                        display_only: true,
                        image: firstDipItem.image || null,
                        description:
                            "Oozy Cheese — MVR 15 • Classic Alifano — MVR 15 • Macardo Sauce — MVR 15",
                    });
                }

                const cleanCategories = (res.data.categories || []).filter(
                    (cat) => cat !== "Crust Options",
                );

                setMenu(cleanItems);
                setCategories(cleanCategories);
                setActiveCategory(cleanCategories[0] || null);
            })
            .catch(() => toast.error("Couldn't load the menu"))
            .finally(() => setLoading(false));
    }, []);

    const grouped = useMemo(() => {
        const map = {};

        for (const item of menu) {
            if (item.category === "Crust Options") continue;
            (map[item.category] = map[item.category] || []).push(item);
        }

        return map;
    }, [menu]);

    const trimmedQuery = query.trim().toLowerCase();
    const isSearching = trimmedQuery.length > 0;

    const searchResults = useMemo(() => {
        if (!isSearching) return [];

        return menu.filter(
            (i) =>
                i.category !== "Crust Options" &&
                (i.name.toLowerCase().includes(trimmedQuery) ||
                    i.description.toLowerCase().includes(trimmedQuery) ||
                    i.category.toLowerCase().includes(trimmedQuery)),
        );
    }, [menu, trimmedQuery, isSearching]);

    useEffect(() => {
        if (categories.length === 0) return;

        const onScroll = () => {
            const scrollY = window.scrollY + 200;
            let current = categories[0];

            for (const cat of categories) {
                const el = sectionRefs.current[cat];
                if (el && el.offsetTop <= scrollY) current = cat;
            }

            setActiveCategory(current);
        };

        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, [categories]);

    const handleAdd = (item) => {
        if (item.display_only) return;

        if (item.category === "Pizza") {
            setSelectedPizza(item);
            setSelectedCrust(CRUST_OPTIONS[0]);
            return;
        }

        addItem(item);
        toast.success(`Added "${item.name}" to cart`);
    };

    const handleAddPizzaWithCrust = () => {
        if (!selectedPizza) return;

        const pizzaWithCrust = {
            ...selectedPizza,
            id: `${selectedPizza.id}-${selectedCrust.id}`,
            name:
                selectedCrust.price === 0
                    ? selectedPizza.name
                    : `${selectedPizza.name} - ${selectedCrust.name}`,
            description:
                selectedCrust.price === 0
                    ? `${selectedPizza.description} Crust: No Crust.`
                    : `${selectedPizza.description} Crust: ${selectedCrust.name}.`,
            price: selectedPizza.price + selectedCrust.price,
            crust: selectedCrust,
        };

        addItem(pizzaWithCrust);

        if (selectedCrust.price === 0) {
            toast.success(`Added "${selectedPizza.name}" to cart`);
        } else {
            toast.success(
                `Added "${selectedPizza.name}" with ${selectedCrust.name} to cart`,
            );
        }

        setSelectedPizza(null);
    };

    const handleAddRiceBowl = (posterItem) => {
        const itemToAdd = {
            id: selectedRiceBowl.id,
            name: selectedRiceBowl.name,
            category: "Rice Bowls",
            description: "Rice bowl selected from the Rice Bowls menu.",
            price: selectedRiceBowl.price,
            image: posterItem.image,
        };

        addItem(itemToAdd);
        toast.success(`Added "${selectedRiceBowl.name}" to cart`);
    };

    const handleAddDip = (posterItem) => {
        const itemToAdd = {
            id: selectedDip.id,
            name: selectedDip.name,
            category: "Dips",
            description: "Dip selected from the Dips menu.",
            price: selectedDip.price,
            image: posterItem.image,
        };

        addItem(itemToAdd);
        toast.success(`Added "${selectedDip.name}" to cart`);
    };

    const handleSecretLogoClick = () => {
        const nextClicks = logoClicks + 1;
        setLogoClicks(nextClicks);

        if (nextClicks >= 5) {
            setLogoClicks(0);
            navigate("/admin");
        }
    };

    const scrollToCategory = (cat) => {
        const el = sectionRefs.current[cat];

        if (el) {
            const top =
                el.getBoundingClientRect().top + window.scrollY - 140;
            window.scrollTo({ top, behavior: "smooth" });
        }
    };

    const RiceBowlsPoster = ({ item }) => (
        <div className="max-w-5xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
                <img
                    src={item.image}
                    alt="Rice Bowls menu"
                    className="w-full h-auto object-contain bg-[#e5e7eb]"
                />

                <div className="p-5 sm:p-7">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                        <div>
                            <h3 className="font-display text-4xl sm:text-5xl text-pz-text leading-none">
                                Rice Bowls
                            </h3>

                            <p className="text-pz-textMuted mt-3 text-sm sm:text-base leading-relaxed">
                                Choose one rice bowl below, then add it to your
                                cart.
                            </p>
                        </div>

                        <div className="font-display text-4xl text-pz-primary leading-none">
                            MVR {selectedRiceBowl.price}
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {RICE_BOWL_OPTIONS.map((option) => {
                            const active = selectedRiceBowl.id === option.id;

                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => setSelectedRiceBowl(option)}
                                    className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${
                                        active
                                            ? "border-pz-primary bg-pz-primary text-white shadow-sm"
                                            : "border-gray-200 bg-[#f3f4f6] text-pz-text hover:border-pz-primary"
                                    }`}
                                >
                                    <span className="font-semibold text-sm sm:text-base">
                                        {option.name.replace(" Rice Bowl", "")}
                                    </span>

                                    <span
                                        className={`font-bold text-sm whitespace-nowrap ${
                                            active
                                                ? "text-white"
                                                : "text-pz-primary"
                                        }`}
                                    >
                                        MVR {option.price}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={() => handleAddRiceBowl(item)}
                        className="mt-6 w-full rounded-full bg-pz-text text-white py-4 font-bold uppercase tracking-wider hover:bg-pz-primary transition-colors inline-flex items-center justify-center gap-2"
                    >
                        <Plus size={18} />
                        Add Rice Bowl
                    </button>
                </div>
            </div>
        </div>
    );

    const DipsPoster = ({ item }) => (
        <div className="max-w-5xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
                <div className="p-5 sm:p-7">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                        <div>
                            <h3 className="font-display text-4xl sm:text-5xl text-pz-text leading-none">
                                Dips
                            </h3>

                            <p className="text-pz-textMuted mt-3 text-sm sm:text-base leading-relaxed">
                                Choose one dip below, then add it to your cart.
                            </p>
                        </div>

                        <div className="font-display text-4xl text-pz-primary leading-none">
                            MVR {selectedDip.price}
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {DIPS_OPTIONS.map((option) => {
                            const active = selectedDip.id === option.id;

                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => setSelectedDip(option)}
                                    className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${
                                        active
                                            ? "border-pz-primary bg-pz-primary text-white shadow-sm"
                                            : "border-gray-200 bg-[#f3f4f6] text-pz-text hover:border-pz-primary"
                                    }`}
                                >
                                    <span className="font-semibold text-sm sm:text-base">
                                        {option.name}
                                    </span>

                                    <span
                                        className={`font-bold text-sm whitespace-nowrap ${
                                            active
                                                ? "text-white"
                                                : "text-pz-primary"
                                        }`}
                                    >
                                        MVR {option.price}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={() => handleAddDip(item)}
                        className="mt-6 w-full rounded-full bg-pz-text text-white py-4 font-bold uppercase tracking-wider hover:bg-pz-primary transition-colors inline-flex items-center justify-center gap-2"
                    >
                        <Plus size={18} />
                        Add Dip
                    </button>
                </div>
            </div>
        </div>
    );

    const renderMenuItem = (item, idx) => {
        if (item.display_only || item.category === "Rice Bowls") {
            if (item.category === "Dips") {
                return <DipsPoster item={item} />;
            }

            return <RiceBowlsPoster item={item} />;
        }

        if (item.category === "Dips") {
            return <DipsPoster item={item} />;
        }

        return (
            <div
                key={item.id}
                style={{
                    animationDelay: `${(idx % 6) * 50}ms`,
                }}
                className="animate-float-up"
            >
                <MenuItemCard item={item} onAdd={handleAdd} />
            </div>
        );
    };

    return (
        <div
            className="min-h-screen bg-[#f3f4f6] paper-grain"
            data-testid="menu-page"
        >
            {/* Header */}
            <header className="sticky top-0 z-40 bg-[#f9fafb]/95 backdrop-blur-xl border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 flex items-center justify-between gap-3">
                    <div
                        data-testid="menu-back-btn"
                        className="flex items-center gap-3 text-pz-text min-w-0"
                    >
                        <button
                            type="button"
                            onClick={handleSecretLogoClick}
                            data-testid="menu-secret-logo-btn"
                            className="flex items-center hover:opacity-80 transition-opacity min-w-0"
                            aria-label="Pizano logo"
                        >
                            <img
                                src="/images/pizano-logo.png"
                                alt="Pizano Italiano"
                                className="h-8 sm:h-10 w-auto object-contain shrink-0"
                            />
                        </button>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                        <Link
                            to="/track"
                            data-testid="menu-track-link"
                            className="inline-flex text-[11px] sm:text-sm font-semibold text-pz-text hover:text-pz-primary uppercase tracking-wider whitespace-nowrap"
                        >
                            <span className="sm:hidden">Track</span>
                            <span className="hidden sm:inline">
                                Track Order
                            </span>
                        </Link>

                        <button
                            onClick={() => setIsOpen(true)}
                            data-testid="open-cart-btn"
                            className="relative inline-flex items-center gap-2 bg-pz-text text-white rounded-full px-3 sm:px-4 py-2.5 font-semibold uppercase tracking-wider text-xs hover:bg-pz-primary transition-colors"
                        >
                            <ShoppingBag size={16} />
                            <span className="hidden sm:inline">Cart</span>

                            {count > 0 && (
                                <span
                                    data-testid="cart-count-badge"
                                    className="ml-1 bg-pz-primary text-white rounded-full min-w-[1.25rem] h-5 grid place-items-center text-[10px] px-1.5"
                                >
                                    {count}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Category tabs */}
                {categories.length > 0 && !isSearching && (
                    <nav
                        data-testid="category-nav"
                        className="border-t border-gray-200 overflow-x-auto"
                    >
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-2 py-2 whitespace-nowrap">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => scrollToCategory(cat)}
                                    data-testid={`category-tab-${cat}`}
                                    className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold transition-all shrink-0 ${
                                        activeCategory === cat
                                            ? "bg-pz-primary text-white"
                                            : "bg-white text-pz-text border border-gray-200 hover:border-pz-primary"
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </nav>
                )}
            </header>

            {/* Top Brand Banner */}
            <section className="w-full bg-[#f3f4f6] border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 text-center">
                    <p className="text-xs sm:text-sm uppercase tracking-[0.45em] sm:tracking-[0.55em] text-pz-primary font-semibold mb-5">
                        Authentic Italian — Malé, Maldives
                    </p>

                    <h1 className="font-display text-6xl sm:text-8xl lg:text-9xl text-pz-text leading-none tracking-tight">
                        Pizano{" "}
                        <span className="text-pz-primary">
                            Italiano
                        </span>
                    </h1>

                    <p className="max-w-4xl mx-auto text-pz-textMuted mt-6 text-lg sm:text-2xl leading-relaxed">
                        Loaded dirty fries, wood-fired pizzas & cozy comfort
                        food. Order for delivery or pickup — straight from our
                        kitchen to your table.
                    </p>
                </div>
            </section>

            {/* Hero */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
                <div className="text-center max-w-3xl mx-auto animate-float-up">
                    <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-pz-primary font-semibold mb-3">
                        — Our Full Menu —
                    </p>

                    <h1 className="font-display text-5xl sm:text-7xl text-pz-text leading-none">
                        Buon <span className="text-pz-primary">Appetito</span>
                    </h1>

                    <p className="text-pz-textMuted mt-4 text-base sm:text-lg">
                        Loaded fries, wood-fired pizza, juicy burgers, smoky
                        kebabs, fresh subs, and handcrafted drinks &
                        desserts.
                    </p>

                    {/* Search bar */}
                    <div className="mt-8 max-w-xl mx-auto">
                        <div className="relative bg-white border border-gray-200 rounded-full shadow-sm focus-within:border-pz-primary focus-within:ring-2 focus-within:ring-pz-primary/20 transition-all">
                            <Search
                                size={18}
                                className="absolute left-5 top-1/2 -translate-y-1/2 text-pz-textMuted pointer-events-none"
                            />

                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search menu — try 'cheese', 'spicy', 'pizza'…"
                                data-testid="menu-search-input"
                                className="w-full bg-transparent pl-12 pr-12 py-3.5 text-pz-text placeholder:text-pz-textMuted outline-none rounded-full text-sm sm:text-base"
                            />

                            {query && (
                                <button
                                    onClick={() => setQuery("")}
                                    data-testid="menu-search-clear"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-full text-pz-textMuted hover:bg-[#f3f4f6] hover:text-pz-text transition-colors"
                                    aria-label="Clear search"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Sections */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-32 space-y-16">
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="bg-white rounded-2xl border border-gray-200 h-96 animate-pulse"
                            />
                        ))}
                    </div>
                ) : isSearching ? (
                    <div data-testid="search-results">
                        <div className="flex items-end justify-between mb-6 border-b border-gray-200 pb-3">
                            <div>
                                <p className="text-xs uppercase tracking-[0.4em] text-pz-primary font-semibold mb-1">
                                    — Search —
                                </p>

                                <h2 className="font-display text-4xl sm:text-5xl text-pz-text leading-none">
                                    Results for "{query.trim()}"
                                </h2>
                            </div>

                            <span className="text-pz-textMuted text-sm">
                                {searchResults.length} match
                                {searchResults.length !== 1 ? "es" : ""}
                            </span>
                        </div>

                        {searchResults.length === 0 ? (
                            <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
                                <Search
                                    size={32}
                                    className="mx-auto text-pz-textMuted mb-3"
                                />

                                <p className="text-pz-text font-semibold mb-1">
                                    No items match your search
                                </p>

                                <p className="text-pz-textMuted text-sm">
                                    Try a different keyword like "chicken",
                                    "cheese", or "iced".
                                </p>
                            </div>
                        ) : (
                            <div
                                className={
                                    searchResults.some(
                                        (item) =>
                                            item.display_only ||
                                            item.category === "Rice Bowls" ||
                                            item.category === "Dips",
                                    )
                                        ? "space-y-6"
                                        : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                                }
                            >
                                {searchResults.map((item, idx) => (
                                    <div key={item.id}>
                                        {renderMenuItem(item, idx)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    categories.map((cat) => {
                        if (cat === "Crust Options") return null;

                        const items = grouped[cat] || [];
                        if (items.length === 0) return null;

                        const isRiceBowls = cat === "Rice Bowls";
                        const isDips = cat === "Dips";

                        return (
                            <div
                                key={cat}
                                ref={(el) => (sectionRefs.current[cat] = el)}
                                data-testid={`category-section-${cat}`}
                                className="scroll-mt-32"
                            >
                                <div className="flex items-end justify-between mb-6 border-b border-gray-200 pb-3">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.4em] text-pz-primary font-semibold mb-1">
                                            — Section —
                                        </p>

                                        <h2 className="font-display text-4xl sm:text-5xl text-pz-text leading-none">
                                            {cat}
                                        </h2>
                                    </div>

                                    <span className="text-pz-textMuted text-sm">
                                        {isRiceBowls || isDips
                                            ? "Choose one"
                                            : `${items.length} item${
                                                  items.length !== 1 ? "s" : ""
                                              }`}
                                    </span>
                                </div>

                                <div
                                    className={
                                        isRiceBowls || isDips
                                            ? "space-y-6"
                                            : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                                    }
                                >
                                    {isDips ? (
                                        <div key="dips-single-selector">
                                            <DipsPoster item={items[0]} />
                                        </div>
                                    ) : (
                                        items.map((item, idx) => (
                                            <div key={item.id}>
                                                {renderMenuItem(item, idx)}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </section>

            <Dialog
                open={!!selectedPizza}
                onOpenChange={(open) => {
                    if (!open) setSelectedPizza(null);
                }}
            >
                <DialogContent className="bg-white rounded-3xl max-w-lg p-0 overflow-hidden border border-gray-200">
                    {selectedPizza && (
                        <>
                            <DialogHeader className="p-5 sm:p-6 border-b border-gray-200">
                                <DialogTitle className="font-display text-4xl text-pz-text leading-none">
                                    Choose Crust
                                </DialogTitle>
                                <p className="text-sm text-pz-textMuted mt-2">
                                    {selectedPizza.name} starts at MVR {selectedPizza.price}.
                                    Pick a crust before adding it to cart.
                                </p>
                            </DialogHeader>

                            <div className="p-5 sm:p-6 space-y-3">
                                {CRUST_OPTIONS.map((option) => {
                                    const active = selectedCrust.id === option.id;

                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            onClick={() => setSelectedCrust(option)}
                                            className={`w-full flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-all ${
                                                active
                                                    ? "border-pz-primary bg-pz-primary text-white shadow-sm"
                                                    : "border-gray-200 bg-[#f3f4f6] text-pz-text hover:border-pz-primary"
                                            }`}
                                        >
                                            <span className="font-semibold text-sm sm:text-base">
                                                {option.name}
                                            </span>

                                            <span
                                                className={`font-bold text-sm whitespace-nowrap ${
                                                    active
                                                        ? "text-white"
                                                        : "text-pz-primary"
                                                }`}
                                            >
                                                {option.price === 0
                                                    ? "Included"
                                                    : `+ MVR ${option.price}`}
                                            </span>
                                        </button>
                                    );
                                })}

                                <div className="flex items-center justify-between rounded-2xl bg-[#f3f4f6] px-4 py-4 mt-4">
                                    <span className="text-sm font-semibold text-pz-text uppercase tracking-wider">
                                        Total
                                    </span>
                                    <span className="font-display text-3xl text-pz-primary leading-none">
                                        MVR {selectedPizza.price + selectedCrust.price}
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleAddPizzaWithCrust}
                                    className="w-full rounded-full bg-pz-text text-white py-4 font-bold uppercase tracking-wider hover:bg-pz-primary transition-colors inline-flex items-center justify-center gap-2"
                                >
                                    <Plus size={18} />
                                    Add Pizza To Cart
                                </button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <CartDrawer />
            <ChatbotWidget />
        </div>
    );
}
